import { useMutation } from 'react-query'

import { splitParagraph } from '../components/ClipEditor/RichTextEditor/Plugins/utils'
import { PartOfSpeech } from '../components/ClipEditor/RichTextEditor/types'
import { HeteronymLanguage } from '../constants/heteronyms'
import { CSRF } from '../utils/CSRF'
import { analysisAPI } from '.'
import { BaseRepository } from './BaseRepository'

export class AnalysisAPI extends BaseRepository {
  getPartsOfSpeech(sentence: string, language: HeteronymLanguage) {
    return super.getHttpClientInstance().post<SentenceAnalysisResponse>(
      '/analysis/parts-of-speech',
      {
        sentence: sentence,
        language: language,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
      },
    )
  }

  localizeTranslate({ ssml, targetLanguage, style }: LocalizeTranslateMutationProps) {
    return super.getHttpClientInstance().post<{
      status: string
      item: {
        ssml: string
      }
    }>(
      '/analysis/translate',
      {
        text: ssml,
        target_language: targetLanguage,
        style,
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
        Accept: 'application/json',
      },
    )
  }
}

type LocalizeTranslateMutationProps = {
  ssml: string
  targetLanguage: string
  style?: string
}

export const useLocalizeTranslateMutation = () => {
  return useMutation(async (props: LocalizeTranslateMutationProps) => {
    const paragraphs = splitParagraph(props.ssml, 700)

    const translatedSsml = (
      await Promise.all(
        paragraphs.map(async (p) => {
          const maxRetries = 3
          let retries = 0
          while (retries < maxRetries) {
            const translation = await analysisAPI.localizeTranslate({ ...props, ssml: p })
            if (translation.data.status === 'success') {
              return translation.data.item.ssml
            }
            retries++
          }

          throw new Error('Failed to translate')
        }),
      )
    ).join(' ')

    return {
      status: 'success',
      item: {
        ssml: translatedSsml,
      },
    }
  })
}

type SentenceAnalysisResponse = SentenceAnalysisError | SentenceAnalysis

export interface SentenceAnalysisError {
  success: false
  message: string
}

export interface SentenceAnalysis {
  success: true
  tokens: AnalysisToken[]
}

export interface AnalysisToken {
  word: string
  pos: string
  tag: PartOfSpeech
}
